import { calculateXirr } from '../../internal-rate-of-return/calculateXirr';

/**
 * Investment analysis results
 */
interface InvestmentAnalysis {
  investmentNPV: number;
  totalReturns: number;
  cagr: number;
  internalRateOfReturn: number | undefined;
  yearsToLastReturn: number;
  isIrrSuspect: boolean;
}

/**
 * Sensitivity analysis result for different discount rates
 */
interface SensitivityResult {
  discountRate: number;
  cagr: number;
}

/**
 * Tolerance used when comparing IRR against zero. Small enough that legitimate
 * near-zero results are not flagged, but large enough to absorb floating-point
 * noise from the XIRR solver.
 */
const IRR_ZERO_TOLERANCE = 1e-6;

/**
 * Calculate NPV of investment payments.
 *
 * `CashFlow.year` is an absolute calendar year, so we discount each entry
 * relative to the earliest investment year.
 */
export function calculateInvestmentNPV(investments: CashFlow[], discountRate: number): number {
  if (investments.length === 0) return 0;
  const earliestYear = Math.min(...investments.map(i => i.year));
  return investments.reduce((npv, investment) => {
    const offset = investment.year - earliestYear;
    return npv + investment.amount / Math.pow(1 + discountRate, offset);
  }, 0);
}

/**
 * Calculate IRR for investment cash flows using XIRR.
 *
 * `CashFlow.year` is an absolute calendar year, used directly as the date.
 */
function calculateInvestmentIRR(
  investments: CashFlow[],
  returns: CashFlow[],
  discountRate: number,
): number | undefined {
  if (investments.length === 0 && returns.length === 0) {
    return 0;
  }

  try {
    const xirrCashFlows: Array<{ amount: number; date: Date }> = [
      ...investments.map(cf => ({
        amount: -(cf.amount ?? 0),
        date: new Date(cf.year, 0, 1),
      })),
      ...returns.map(cf => ({
        amount: cf.amount ?? 0,
        date: new Date(cf.year, 0, 1),
      })),
    ];

    return calculateXirr(xirrCashFlows, {
      initialGuess: 0.1,
      tolerance: 1e-10,
      maxIterations: 100,
      useBracketing: true,
    });
  } catch (error) {
    // If XIRR calculation fails, try with lumped investments using NPV at the
    // earliest investment year.
    try {
      const npvInvestments = calculateInvestmentNPV(investments, discountRate);
      const earliestYear = Math.min(...investments.map(i => i.year));
      const xirrCashFlows: Array<{ amount: number; date: Date }> = [
        { amount: -npvInvestments, date: new Date(earliestYear, 0, 1) },
        ...returns.map(cf => ({
          amount: cf.amount ?? 0,
          date: new Date(cf.year, 0, 1),
        })),
      ];

      return calculateXirr(xirrCashFlows, {
        initialGuess: 0.1,
        tolerance: 1e-10,
        maxIterations: 100,
        useBracketing: true,
      });
    } catch (fallbackError) {
      return undefined;
    }
  }
}

/**
 * Detect whether an IRR value is likely spurious due to multiple mathematical roots.
 *
 * Background: IRR is the root of an N-degree polynomial in (1+r). By Descartes'
 * rule of signs, the number of positive real roots is bounded by the number of
 * sign changes in the cashflow series. With "non-conventional" cashflows (2+
 * sign changes — e.g. a policy whose premium payments overlap with annuity
 * payouts) the polynomial can have multiple valid IRRs, and the XIRR solver
 * returns whichever root it converges to first. The number it reports is then
 * essentially arbitrary and shouldn't be shown to the user.
 *
 * Two checks:
 *
 *   Check 1 — Sign contradiction: For "pay first, receive later" cashflows
 *     (the standard funding-source shape), NPV is monotonically decreasing in
 *     the discount rate, so totalReturns > totalCost ⟺ IRR > 0. Any
 *     contradiction means the solver returned a wrong root or the cashflow
 *     shape is non-conventional.
 *
 *   Check 2 — Multiple sign changes: A direct test for the multi-root
 *     condition via Descartes' rule of signs.
 */
export function isIrrSuspect(
  irr: number | undefined,
  investments: CashFlow[],
  returns: CashFlow[],
  totalCost: number,
  totalReturns: number,
): boolean {
  if (irr === undefined) return false;

  // Check 1: Sign contradiction. In this codebase every funding source has
  // strictly "pay first, receive later" cashflows, so any sign mismatch
  // between IRR and the nominal gain/loss indicates a spurious solver result.
  if (totalReturns > totalCost && irr < -IRR_ZERO_TOLERANCE) return true;
  if (totalReturns < totalCost && irr > IRR_ZERO_TOLERANCE) return true;

  // Check 2: Multiple sign changes in the merged cashflow series.
  const merged = mergeCashflowsByYear(investments, returns);

  let signChanges = 0;
  let lastSign = 0;
  for (const amount of merged) {
    if (amount === 0) continue;
    const sign = amount > 0 ? 1 : -1;
    if (lastSign !== 0 && sign !== lastSign) signChanges++;
    lastSign = sign;
  }

  return signChanges > 1;
}

/**
 * Merge investments (negated) and returns by absolute year, returning the
 * net amount for each year sorted chronologically.
 */
function mergeCashflowsByYear(investments: CashFlow[], returns: CashFlow[]): number[] {
  const byYear = new Map<number, number>();
  for (const cf of investments) {
    byYear.set(cf.year, (byYear.get(cf.year) ?? 0) - cf.amount);
  }
  for (const cf of returns) {
    byYear.set(cf.year, (byYear.get(cf.year) ?? 0) + cf.amount);
  }
  return Array.from(byYear.entries())
    .sort(([a], [b]) => a - b)
    .map(([, amount]) => amount);
}

/**
 * Analyze investment returns
 */
export function analyzeInvestmentReturns(
  investments: CashFlow[],
  returns: CashFlow[],
  discountRateWholeNumber: number = 0,
  calculateIRR: boolean = true,
): InvestmentAnalysis {
  const discountRate = discountRateWholeNumber / 100;
  const investmentNPV = calculateInvestmentNPV(investments, discountRate);
  const totalReturns = returns.reduce((sum, r) => sum + r.amount, 0);
  const yearsToLastReturn = returns.length > 0 ? returnsSpan(investments, returns) : 0;

  if (investmentNPV <= 0) {
    return {
      investmentNPV: 0,
      totalReturns,
      cagr: Infinity,
      internalRateOfReturn: calculateIRR ? Infinity : undefined,
      yearsToLastReturn,
      isIrrSuspect: false,
    };
  }

  const cagr = Math.pow(totalReturns / investmentNPV, 1 / yearsToLastReturn) - 1;

  const IRR = calculateIRR
    ? calculateInvestmentIRR(investments, returns, discountRate)
    : undefined;

  const totalInvested = investments.reduce((sum, i) => sum + i.amount, 0);
  const suspect = isIrrSuspect(IRR, investments, returns, totalInvested, totalReturns);

  return {
    investmentNPV,
    totalReturns,
    cagr,
    internalRateOfReturn: IRR,
    yearsToLastReturn,
    isIrrSuspect: suspect,
  };
}

/**
 * Calculate the number of years between the earliest investment and the last
 * return. Used as the CAGR exponent.
 */
function returnsSpan(investments: CashFlow[], returns: CashFlow[]): number {
  if (returns.length === 0) return 0;
  const earliestInvestmentYear =
    investments.length > 0 ? Math.min(...investments.map(i => i.year)) : returns[0].year;
  const lastReturnYear = Math.max(...returns.map(r => r.year));
  return Math.max(1, lastReturnYear - earliestInvestmentYear + 1);
}

/**
 * Perform sensitivity analysis
 */
export function performSensitivityAnalysis(
  investments: CashFlow[],
  returns: CashFlow[],
  discountRates: number[],
): SensitivityResult[] {
  const yearsToLastReturn = returnsSpan(investments, returns);
  const totalReturns = returns.reduce((sum, r) => sum + r.amount, 0);

  return discountRates.map(rate => {
    const npv = calculateInvestmentNPV(investments, rate);
    const cagr = Math.pow(totalReturns / npv, 1 / yearsToLastReturn) - 1;

    return {
      discountRate: rate,
      cagr,
    };
  });
}
